import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../auth/AuthContext';
import { useToast } from './Toast';
import { extractErrorMessage } from '../utils/errors';

const API_URL = process.env.REACT_APP_API_URL || '/api';

export interface ApprovalRequest {
  id: string;
  createdAtUtc: string;
  requestedBy: string;
  requesterMode: string;
  operationType: string;
  targetSummary: string;
  reason: string;
  status: 'Pending' | 'Rejected' | 'Executed' | 'Failed';
  decidedBy?: string;
  decidedAtUtc?: string;
  decisionReason?: string;
  resultMessage?: string;
}

const STATUS_STYLE: Record<string, { bg: string; fg: string; border: string }> = {
  Pending:  { bg: '#ffc10722', fg: '#ffe08a', border: '#ffc10744' },
  Executed: { bg: '#28a74522', fg: '#7ee2a8', border: '#28a74544' },
  Rejected: { bg: '#6c757d22', fg: '#cfd3d7', border: '#6c757d44' },
  Failed:   { bg: '#dc354522', fg: '#ff8f9a', border: '#dc354544' },
};

const FILTERS = ['Pending', 'Executed', 'Rejected', 'Failed', 'all'] as const;

const ApprovalsPanel: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const isAdmin = user?.role === 'admin';

  const [requests, setRequests] = useState<ApprovalRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>('Pending');
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === 'all' ? {} : { status: filter };
      const res = await axios.get<ApprovalRequest[]>(`${API_URL}/approvals`, { params });
      setRequests(res.data);
    } catch (e) {
      showToast(extractErrorMessage(e, 'Failed to load approval requests.'), 'error');
    } finally {
      setLoading(false);
    }
  }, [filter, showToast]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [load]);

  const approve = async (id: string) => {
    setBusyId(id);
    try {
      const res = await axios.post<ApprovalRequest>(`${API_URL}/approvals/${id}/approve`);
      showToast(
        res.data.status === 'Executed'
          ? `Approved and executed: ${res.data.resultMessage}`
          : `Approved but execution failed: ${res.data.resultMessage}`,
        res.data.status === 'Executed' ? 'success' : 'error'
      );
      await load();
    } catch (e) {
      showToast(extractErrorMessage(e, 'Approval failed.'), 'error');
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (id: string) => {
    setBusyId(id);
    try {
      await axios.post(`${API_URL}/approvals/${id}/reject`, { reason: rejectReason });
      showToast('Request rejected.', 'info');
      setRejectingId(null);
      setRejectReason('');
      await load();
    } catch (e) {
      showToast(extractErrorMessage(e, 'Rejection failed.'), 'error');
    } finally {
      setBusyId(null);
    }
  };

  const badge = (status: string) => {
    const s = STATUS_STYLE[status] || STATUS_STYLE.Pending;
    return (
      <span style={{ background: s.bg, color: s.fg, border: `1px solid ${s.border}`, borderRadius: 10, padding: '0.2rem 0.6rem', fontSize: '0.75rem', fontWeight: 600, flexShrink: 0 }}>
        {status}
      </span>
    );
  };

  return (
    <div>
      <div style={{ marginBottom: '1rem', color: '#888', fontSize: '0.9rem' }}>
        {isAdmin
          ? 'Change requests submitted by operators. Approving executes the change immediately (you cannot approve your own requests).'
          : 'Your submitted change requests. High-risk operations execute after an administrator approves them.'}
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        {FILTERS.map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{ padding: '0.5rem 1rem', background: filter === f ? '#007bff' : '#25252a', color: filter === f ? 'white' : '#b0b0b0', border: '1px solid ' + (filter === f ? '#007bff' : '#444'), borderRadius: '6px', cursor: 'pointer' }}
          >
            {f === 'all' ? 'All' : f}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <button onClick={load} style={{ padding: '0.5rem 1rem', background: '#25252a', color: '#b0b0b0', border: '1px solid #444', borderRadius: '6px', cursor: 'pointer' }}>
          {loading ? 'Loading...' : '↻ Refresh'}
        </button>
      </div>

      {requests.length === 0 ? (
        <div style={{ background: '#25252a', border: '1px solid #444', borderRadius: 8, padding: '3rem', textAlign: 'center', color: '#888' }}>
          {loading ? 'Loading...' : filter === 'Pending' ? 'No pending change requests.' : 'No requests match this filter.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {requests.map(r => (
            <div key={r.id} style={{ background: '#25252a', border: '1px solid #444', borderRadius: 8, padding: '0.85rem 1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                {badge(r.status)}
                <span style={{ background: '#007bff22', color: '#4da6ff', border: '1px solid #007bff44', borderRadius: 4, padding: '0.2rem 0.5rem', fontSize: '0.75rem', fontWeight: 600, flexShrink: 0 }}>
                  {r.operationType}
                </span>
                <span style={{ color: '#f0f0f0', flex: 1, fontSize: '0.9rem', minWidth: 200 }}>{r.targetSummary}</span>
                <span style={{ color: '#888', fontSize: '0.8rem', flexShrink: 0 }}>
                  {r.requestedBy} · {new Date(r.createdAtUtc).toLocaleString()}
                </span>
                {isAdmin && r.status === 'Pending' && (
                  <span style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button
                      className="btn-small btn-primary"
                      disabled={busyId === r.id}
                      onClick={() => approve(r.id)}
                    >
                      {busyId === r.id ? '…' : '✓ Approve & execute'}
                    </button>
                    <button
                      className="btn-small btn-danger"
                      disabled={busyId === r.id}
                      onClick={() => { setRejectingId(rejectingId === r.id ? null : r.id); setRejectReason(''); }}
                    >
                      ✗ Reject
                    </button>
                  </span>
                )}
              </div>

              {rejectingId === r.id && (
                <div style={{ display: 'flex', gap: 8, marginTop: '0.75rem' }}>
                  <input
                    type="text"
                    placeholder="Rejection reason (visible to the requester)"
                    value={rejectReason}
                    onChange={e => setRejectReason(e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <button className="btn-small btn-danger" disabled={busyId === r.id} onClick={() => reject(r.id)}>Confirm reject</button>
                </div>
              )}

              {(r.reason || r.decidedBy || r.resultMessage) && (
                <div style={{ marginTop: '0.6rem', fontSize: '0.8rem', color: '#888', display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {r.reason && <span>Reason: <span style={{ color: '#b0b0b0' }}>{r.reason}</span></span>}
                  {r.decidedBy && (
                    <span>
                      {r.status === 'Rejected' ? 'Rejected' : 'Approved'} by <span style={{ color: '#b0b0b0' }}>{r.decidedBy}</span>
                      {r.decidedAtUtc && ` · ${new Date(r.decidedAtUtc).toLocaleString()}`}
                      {r.decisionReason && <> — <span style={{ color: '#b0b0b0' }}>{r.decisionReason}</span></>}
                    </span>
                  )}
                  {r.resultMessage && (
                    <span>Result: <span style={{ color: r.status === 'Failed' ? '#ff8f9a' : '#b0b0b0' }}>{r.resultMessage}</span></span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ApprovalsPanel;
